import { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import type { Book, ShelfStatus } from '@booknest/shared';

import { BookModal } from '@/features/library/components/BookModal.js';
import {
  Bookcase,
  type BookcaseUnitData,
} from '@/features/library/components/bookcase/Bookcase.js';
import { useMyShelf } from '@/features/library/hooks/useMyShelf.js';

interface UnitConfig {
  readonly status: ShelfStatus;
  readonly label: string;
  readonly emoji: string;
  readonly emptyMessage: string;
}

const PREVIEW_ORDER: ReadonlyArray<UnitConfig> = [
  {
    status: 'reading',
    label: 'Lectures en cours',
    emoji: '🔖',
    emptyMessage: 'Rien en cours pour le moment',
  },
  {
    status: 'wishlist',
    label: 'Wishlist',
    emoji: '💌',
    emptyMessage: 'Wishlist vide — découvre des livres',
  },
];

/**
 * Aperçu compact des étagères du user, intégré dans l'Accueil.
 * Affiche reading + wishlist par défaut. Click sur une tranche → modal
 * détail (même UX que /library).
 */
export function LibraryPreview(): JSX.Element {
  const { data } = useMyShelf();
  const entries = data?.items ?? [];
  const [selectedBookId, setSelectedBookId] = useState<string | null>(null);
  const [openingBookId, setOpeningBookId] = useState<string | null>(null);

  const selectedEntry =
    entries.find((e) => e.book.id === selectedBookId) ?? null;
  const selectedBook = selectedEntry?.book ?? null;

  const handleBookClick = (id: string): void => {
    if (openingBookId !== null) return;
    setOpeningBookId(id);
    window.setTimeout(() => setSelectedBookId(id), 380);
  };

  const handleClose = (): void => {
    setSelectedBookId(null);
    setOpeningBookId(null);
  };

  const units: ReadonlyArray<BookcaseUnitData> = useMemo(() => {
    const byStatus = new Map<ShelfStatus, Book[]>([
      ['reading', []],
      ['wishlist', []],
      ['done', []],
      ['abandoned', []],
    ]);
    for (const e of entries) {
      byStatus.get(e.status)?.push(e.book);
    }
    return PREVIEW_ORDER.map<BookcaseUnitData>((cfg) => ({
      key: cfg.status,
      label: cfg.label,
      emoji: cfg.emoji,
      emptyMessage: cfg.emptyMessage,
      firstAsCover: cfg.status === 'reading',
      books: byStatus.get(cfg.status) ?? [],
    }));
  }, [entries]);

  return (
    <section className="flex flex-col gap-3">
      <div className="flex items-baseline justify-between gap-4">
        <div>
          <h2 className="font-serif text-2xl font-semibold text-brown">
            Ta bibliothèque
          </h2>
          <p className="font-script text-lg text-brown-soft">
            aperçu de tes étagères 📚
          </p>
        </div>
        <Link
          to="/library"
          className="text-sm font-semibold text-terracotta hover:underline"
        >
          Tout voir →
        </Link>
      </div>

      <Bookcase
        units={units}
        openingBookId={openingBookId}
        onBookClick={(b) => handleBookClick(b.id)}
      />

      <p className="text-center font-script text-base text-brown-soft">
        👆 touche une tranche pour ouvrir le livre
      </p>

      {selectedBook && (
        <BookModal
          book={selectedBook}
          entry={selectedEntry}
          onClose={handleClose}
        />
      )}
    </section>
  );
}
